import {
  ChangeEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Dialog } from "primereact/dialog";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import SearchIcon from "@mui/icons-material/Search";
import * as XLSX from "xlsx";
import * as XLSXStyle from "xlsx-js-style";
import "../../../assets/seleccionMasiva/carga-nomina-inhabilitados.scss";
import { checkRut, formatRut } from "../../../hooks/useRut";
import {
  INHABILITADO_SYSTEM_COLUMNS,
  InhabilitadoFieldKey,
  InhabilitadoSheetData,
  extractInhabilitadoSheet,
  readInhabilitadoWorkbook,
  resolveRowComment,
  resolveRowNames,
  resolveRowRut,
  suggestInhabilitadoMapping,
} from "./inhabilitadosNominaExcel";
import {
  ClientOption,
  ExclusionType,
  InhabilitadoCargaResult,
  fetchClients,
  fetchExclusionTypes,
  processInhabilitadosNomina,
} from "../../../services/inhabilitadosNominaService";
import SearchSelect, { SearchSelectOption } from "./SearchSelect";
import MultiSearchSelect from "./MultiSearchSelect";

interface CargaNominaInhabilitadosProps {
  visible: boolean;
  onHide: () => void;
  onLoaded?: () => void;
}

type InhabStep = "mapping" | "asignacion" | "result";

interface AssignmentRow {
  rutRaw: string;
  rutFormateado: string;
  rutValido: boolean;
  nombres: string;
  apellidos: string;
  excTipoId: string;
  clienteIds: string[];
  comentario: string;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const PAGE_SIZE = 50;
const CLIENT_LEVEL = "cliente";

const isClientLevelType = (
  excTipoId: string,
  typesById: Map<string, ExclusionType>
): boolean => {
  const tipo = typesById.get(excTipoId);
  return (tipo?.exclusionLevel ?? "").toLowerCase() === CLIENT_LEVEL;
};

/**
 * Chip arrastrable de una columna del Excel. El elemento original se mantiene
 * en su lugar (atenuado mientras se arrastra); la copia que sigue al cursor la
 * dibuja el `DragOverlay` del componente principal.
 */
const SourceItem = ({
  header,
  mapped,
}: {
  header: string;
  mapped: boolean;
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `src::${header}`,
    data: { header },
  });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`inhab-nomina__source ${mapped ? "is-mapped" : ""} ${
        isDragging ? "is-dragging" : ""
      }`}
    >
      <span className="inhab-nomina__drag-handle">⋮⋮</span>
      <span className="inhab-nomina__source-label">{header}</span>
      {mapped && <CheckCircleOutlineIcon />}
    </div>
  );
};

/** Zona donde se suelta una columna del Excel para hacer el match. */
const TargetSlot = ({
  columnKey,
  mapped,
  available = false,
  children,
}: {
  columnKey: string;
  mapped: boolean;
  /** Resalta el slot como disponible mientras hay un arrastre en curso. */
  available?: boolean;
  children: ReactNode;
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `tgt::${columnKey}`,
    data: { columnKey },
  });
  return (
    <div
      ref={setNodeRef}
      className={`inhab-nomina__target ${mapped ? "is-mapped" : ""} ${
        available ? "is-available" : ""
      } ${isOver ? "is-over" : ""}`}
    >
      {children}
    </div>
  );
};

/**
 * Los campos de nombre se agrupan bajo un único selector de "formato" para no
 * abrumar al usuario con 7 casillas parecidas. Según el formato elegido se
 * muestran solo las columnas de sistema pertinentes y se descartan los matches
 * de los campos ocultos, evitando mapeos duplicados o confusos.
 */
type NameFormat = "completo" | "separado" | "detallado";

const NAME_FIELDS_BY_FORMAT: Record<NameFormat, InhabilitadoFieldKey[]> = {
  completo: ["nombreCompleto"],
  separado: ["nombres", "apellidos"],
  detallado: [
    "primerNombre",
    "segundoNombre",
    "primerApellido",
    "segundoApellido",
  ],
};

const NAME_FORMAT_OPTIONS: {
  value: NameFormat;
  label: string;
  hint: string;
}[] = [
  { value: "completo", label: "1 columna", hint: "Nombre y apellidos juntos" },
  { value: "separado", label: "2 columnas", hint: "Nombres · Apellidos" },
  { value: "detallado", label: "4 columnas", hint: "Cada nombre y apellido" },
];

/**
 * El RUT puede venir en una sola columna (`11222333-K`) o dividido en dos
 * columnas (cuerpo `11222333` + dígito verificador `K`). Según el formato
 * elegido se muestran solo las columnas pertinentes y, en modo "2 columnas",
 * ambas se concatenan como un único RUT `11222333-K`.
 */
type RutFormat = "completo" | "separado";

const RUT_FIELDS_BY_FORMAT: Record<RutFormat, InhabilitadoFieldKey[]> = {
  completo: ["rut"],
  separado: ["rut", "rutDigito"],
};

const RUT_FORMAT_OPTIONS: {
  value: RutFormat;
  label: string;
  hint: string;
}[] = [
  { value: "completo", label: "1 columna", hint: "RUT con dígito verificador" },
  { value: "separado", label: "2 columnas", hint: "RUT · Dígito verificador" },
];

const COLUMN_BY_KEY = new Map(
  INHABILITADO_SYSTEM_COLUMNS.map((column) => [column.key, column])
);

/** Nombres: mayúsculas, sin espacios en los extremos ni dobles internos. */
const toUpperName = (value: string): string =>
  value.trim().replace(/\s+/g, " ").toUpperCase();

/** Comentarios: mayúsculas y sin espacios en los extremos. */
const toUpperComment = (value: string): string => value.trim().toUpperCase();

// --- Estilos del Excel de resultados (xlsx-js-style) ------------------------
const XLSX_BORDER = {
  top: { style: "thin", color: { rgb: "D9E2EC" } },
  bottom: { style: "thin", color: { rgb: "D9E2EC" } },
  left: { style: "thin", color: { rgb: "D9E2EC" } },
  right: { style: "thin", color: { rgb: "D9E2EC" } },
};

const XLSX_HEADER_STYLE = {
  font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: "14396E" } },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border: XLSX_BORDER,
};

const XLSX_CELL_STYLE = {
  alignment: { vertical: "center", wrapText: true },
  border: XLSX_BORDER,
};

/** Relleno/texto por estado, alineado con los colores de la tabla en pantalla. */
const XLSX_ESTADO_STYLE: Record<string, { bg: string; fg: string }> = {
  CREADO: { bg: "C6EFCE", fg: "166534" },
  DUPLICADO: { bg: "E2E8F0", fg: "334155" },
  RUT_INVALIDO: { bg: "F4CCCC", fg: "991B1B" },
  TIPO_INVALIDO: { bg: "F4CCCC", fg: "991B1B" },
  CLIENTE_INVALIDO: { bg: "F4CCCC", fg: "991B1B" },
  CLIENTE_REQUERIDO: { bg: "FCE5CD", fg: "9A3412" },
  COMENTARIO_REQUERIDO: { bg: "FCE5CD", fg: "9A3412" },
  ERROR: { bg: "FCE5CD", fg: "9A3412" },
};

/**
 * Aplica encabezado con color, bordes, zebra y (opcional) coloreado de la
 * columna Estado a una hoja creada con `json_to_sheet`.
 */
const styleResultSheet = (
  sheet: XLSXStyle.WorkSheet,
  options: { widths: number[]; estadoCol?: number; boldFirstCol?: boolean }
): void => {
  const ref = sheet["!ref"];
  if (!ref) return;
  const range = XLSXStyle.utils.decode_range(ref);
  sheet["!cols"] = options.widths.map((wch) => ({ wch }));
  sheet["!rows"] = [{ hpt: 22 }];

  for (let R = range.s.r; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const addr = XLSXStyle.utils.encode_cell({ r: R, c: C });
      const cell = (sheet as any)[addr];
      if (!cell) continue;

      if (R === 0) {
        cell.s = XLSX_HEADER_STYLE;
        continue;
      }

      const zebra = R % 2 === 1 ? { fgColor: { rgb: "F5F8FC" } } : undefined;
      cell.s = { ...XLSX_CELL_STYLE, ...(zebra ? { fill: zebra } : {}) };

      if (options.boldFirstCol && C === 0) {
        cell.s = { ...cell.s, font: { bold: true, color: { rgb: "334155" } } };
      }

      if (options.estadoCol !== undefined && C === options.estadoCol) {
        const estado = String(cell.v ?? "").toUpperCase();
        const tone = XLSX_ESTADO_STYLE[estado];
        if (tone) {
          cell.s = {
            border: XLSX_BORDER,
            font: { bold: true, color: { rgb: tone.fg } },
            fill: { fgColor: { rgb: tone.bg } },
            alignment: { horizontal: "center", vertical: "center" },
          };
        }
      }
    }
  }
};

/** Deduce el formato de nombre a partir de las columnas que hizo match solas. */
const detectNameFormat = (mapping: Record<string, string>): NameFormat => {
  if (
    mapping.primerNombre ||
    mapping.segundoNombre ||
    mapping.primerApellido ||
    mapping.segundoApellido
  )
    return "detallado";
  if (mapping.nombres || mapping.apellidos) return "separado";
  if (mapping.nombreCompleto) return "completo";
  return "separado";
};

/** Deduce el formato de RUT: si mapeó el dígito verificador, es "separado". */
const detectRutFormat = (mapping: Record<string, string>): RutFormat =>
  mapping.rutDigito ? "separado" : "completo";

/** Conserva solo comentario y los campos de RUT y nombre del formato activo. */
const pruneMapping = (
  mapping: Record<string, string>,
  nameFormat: NameFormat,
  rutFormat: RutFormat
): Record<string, string> => {
  const keep = new Set<string>([
    "comentario",
    ...RUT_FIELDS_BY_FORMAT[rutFormat],
    ...NAME_FIELDS_BY_FORMAT[nameFormat],
  ]);
  const next: Record<string, string> = {};
  Object.keys(mapping).forEach((key) => {
    if (keep.has(key)) next[key] = mapping[key];
  });
  return next;
};

const CargaNominaInhabilitados = ({
  visible,
  onHide,
  onLoaded,
}: CargaNominaInhabilitadosProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<InhabStep>("mapping");

  const [fileName, setFileName] = useState("");
  const [workbook, setWorkbook] = useState<XLSX.WorkBook>();
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [sheetData, setSheetData] = useState<InhabilitadoSheetData>();
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [nameFormat, setNameFormat] = useState<NameFormat>("separado");
  const [rutFormat, setRutFormat] = useState<RutFormat>("completo");
  const [search, setSearch] = useState("");
  const [activeHeader, setActiveHeader] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor)
  );

  const [types, setTypes] = useState<ExclusionType[]>([]);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [catalogsError, setCatalogsError] = useState("");

  const [rows, setRows] = useState<AssignmentRow[]>([]);
  const [applyToAll, setApplyToAll] = useState(true);
  const [bulkTipo, setBulkTipo] = useState("");
  const [bulkClientes, setBulkClientes] = useState<string[]>([]);
  const [bulkComentario, setBulkComentario] = useState("");
  const [assignPage, setAssignPage] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<InhabilitadoCargaResult>();
  const [resultFilter, setResultFilter] = useState("all");
  const [resultPage, setResultPage] = useState(0);

  const typesById = useMemo(
    () => new Map(types.map((tipo) => [tipo.excTipoId, tipo])),
    [types]
  );

  // Solo se ofrecen tipos de nivel ALL o CLIENTE; los de nivel BUSQUEDA y AUTO
  // se procesan en otro flujo y no deben aparecer en la carga masiva.
  const typeSelectOptions = useMemo<SearchSelectOption[]>(
    () =>
      types
        .filter((tipo) =>
          ["all", "cliente"].includes((tipo.exclusionLevel ?? "").toLowerCase())
        )
        .map((tipo) => ({
          code: tipo.excTipoId,
          name: tipo.excTipoNombre,
        })),
    [types]
  );
  const clientSelectOptions = useMemo<SearchSelectOption[]>(
    () =>
      clients.map((cliente) => ({
        code: cliente.clienteId,
        name: cliente.clienteDni
          ? `${cliente.clienteNombre} (${cliente.clienteDni})`
          : cliente.clienteNombre,
      })),
    [clients]
  );
  const sheetSelectOptions = useMemo<SearchSelectOption[]>(
    () => sheetNames.map((name) => ({ code: name, name })),
    [sheetNames]
  );
  const headerSelectOptions = useMemo<SearchSelectOption[]>(
    () =>
      (sheetData?.headers ?? []).map((header) => ({
        code: header,
        name: header,
      })),
    [sheetData]
  );

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    (async () => {
      try {
        setCatalogsError("");
        const [tiposResult, clientesResult] = await Promise.all([
          fetchExclusionTypes(),
          fetchClients(),
        ]);
        if (cancelled) return;
        const orderedTypes = [...tiposResult].sort((a, b) =>
          (a.excTipoNombre ?? "").localeCompare(b.excTipoNombre ?? "")
        );
        const orderedClients = [...clientesResult].sort((a, b) =>
          (a.clienteNombre ?? "").localeCompare(b.clienteNombre ?? "")
        );
        setTypes(orderedTypes);
        setClients(orderedClients);
      } catch (_) {
        if (!cancelled)
          setCatalogsError(
            "No fue posible cargar los tipos de inhabilitación o los clientes."
          );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible]);

  const reset = () => {
    setStep("mapping");
    setFileName("");
    setWorkbook(undefined);
    setSheetNames([]);
    setSelectedSheet("");
    setSheetData(undefined);
    setMapping({});
    setNameFormat("separado");
    setRutFormat("completo");
    setSearch("");
    setActiveHeader("");
    setRows([]);
    setApplyToAll(true);
    setBulkTipo("");
    setBulkClientes([]);
    setBulkComentario("");
    setAssignPage(0);
    setLoading(false);
    setError("");
    setResult(undefined);
    setResultFilter("all");
    setResultPage(0);
    if (inputRef.current) inputRef.current.value = "";
  };

  const close = () => {
    reset();
    onHide();
  };

  const selectSheet = (name: string, sourceWorkbook = workbook) => {
    setSelectedSheet(name);
    setError("");
    if (!name || !sourceWorkbook) {
      setSheetData(undefined);
      setMapping({});
      return;
    }
    try {
      const data = extractInhabilitadoSheet(sourceWorkbook, name);
      setSheetData(data);
      const suggested = suggestInhabilitadoMapping(data.headers);
      const format = detectNameFormat(suggested);
      const rutFmt = detectRutFormat(suggested);
      setNameFormat(format);
      setRutFormat(rutFmt);
      setMapping(pruneMapping(suggested, format, rutFmt));
    } catch (sheetError: any) {
      setSheetData(undefined);
      setMapping({});
      setError(sheetError?.message ?? "No se pudo leer la hoja seleccionada.");
    }
  };

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    reset();
    if (!/\.xlsx?$/i.test(file.name)) {
      setError("Selecciona un archivo Excel con extensión .xlsx o .xls.");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError("El archivo supera el tamaño máximo permitido de 10 MB.");
      return;
    }
    try {
      const nextWorkbook = await readInhabilitadoWorkbook(file);
      setFileName(file.name);
      setWorkbook(nextWorkbook);
      setSheetNames(nextWorkbook.SheetNames);
      if (nextWorkbook.SheetNames.length === 1)
        selectSheet(nextWorkbook.SheetNames[0], nextWorkbook);
    } catch (_) {
      setError(
        "No se pudo abrir el archivo. Verifica que sea un Excel válido y no esté protegido."
      );
    }
  };

  const assignSource = (target: InhabilitadoFieldKey, source: string) => {
    setMapping((current) => {
      const next = { ...current };
      Object.keys(next).forEach((key) => {
        if (next[key] === source && key !== target) delete next[key];
      });
      if (source) next[target] = source;
      else delete next[target];
      return next;
    });
  };

  const changeNameFormat = (format: NameFormat) => {
    setNameFormat(format);
    setMapping((current) => pruneMapping(current, format, rutFormat));
  };

  const changeRutFormat = (format: RutFormat) => {
    setRutFormat(format);
    setMapping((current) => pruneMapping(current, nameFormat, format));
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveHeader((event.active.data.current?.header as string) ?? "");
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const header = event.active.data.current?.header as string | undefined;
    const columnKey = event.over?.data.current?.columnKey as
      | InhabilitadoFieldKey
      | undefined;
    if (header && columnKey) assignSource(columnKey, header);
    setActiveHeader("");
  };

  const mappedSources = useMemo(
    () => new Set(Object.values(mapping)),
    [mapping]
  );
  const visibleSourceHeaders =
    sheetData?.headers.filter((header) =>
      header.toLowerCase().includes(search.trim().toLowerCase())
    ) ?? [];
  const mappingPairs = INHABILITADO_SYSTEM_COLUMNS.flatMap((column) =>
    mapping[column.key] ? [{ source: mapping[column.key], target: column.key }] : []
  );
  const canAssign =
    !!sheetData?.rows.length &&
    !!mapping.rut &&
    (rutFormat === "completo" || !!mapping.rutDigito);

  const renderTargetSlot = (key: InhabilitadoFieldKey, hideLabel = false) => {
    const column = COLUMN_BY_KEY.get(key);
    if (!column || !sheetData) return null;
    return (
      <TargetSlot
        key={column.key}
        columnKey={column.key}
        mapped={!!mapping[column.key]}
        available={!!activeHeader && !mapping[column.key]}
      >
        <div>
          {!hideLabel && (
            <strong>
              {column.label}
              {column.required && <em> *</em>}
            </strong>
          )}
          {column.description && (
            <small className="inhab-nomina__target-description">
              {column.description}
            </small>
          )}
          <small className="inhab-nomina__target-match">
            {mapping[column.key] || "Suelta una columna aquí"}
          </small>
        </div>
        <SearchSelect
          value={mapping[column.key] || ""}
          options={headerSelectOptions}
          onChange={(code) => assignSource(column.key, code)}
          placeholder="Sin asignar"
          searchPlaceholder="Buscar columna"
          ariaLabel={`Match para ${column.label}`}
        />
      </TargetSlot>
    );
  };

  const buildRows = (): AssignmentRow[] => {
    if (!sheetData) return [];
    return sheetData.rows
      .map((row) => {
        // Concatena RUT + dígito verificador cuando viene en dos columnas.
        const rutRaw = resolveRowRut(row, mapping);
        const resolved = resolveRowNames(row, mapping);
        const excelComment = resolveRowComment(row, mapping);
        const rutValido = checkRut(rutRaw);
        return {
          rutRaw,
          rutFormateado: rutValido ? formatRut(rutRaw).toUpperCase() : rutRaw,
          rutValido,
          nombres: toUpperName(resolved.nombres),
          apellidos: toUpperName(resolved.apellidos),
          excTipoId: "",
          clienteIds: [],
          // El comentario del Excel (si se mapeó) queda seteado por registro.
          comentario: toUpperComment(excelComment),
        };
      })
      .filter((row) => row.rutRaw !== "" && row.rutRaw !== "-");
  };

  const goToAssignment = () => {
    setError("");
    const built = buildRows();
    setRows(built);
    // Si el Excel trae comentarios por fila, arrancamos en modo por-fila para
    // respetarlos; si no, en modo "aplicar a todos".
    const hasExcelComments = built.some((row) => row.comentario.trim() !== "");
    setApplyToAll(!hasExcelComments);
    setAssignPage(0);
    setStep("asignacion");
  };

  // Mientras el switch "Aplicar a todos" esté activo, el tipo y cliente del
  // bloque superior gobiernan todas las filas. El comentario solo sobrescribe
  // cuando se escribe uno en el bloque; si queda vacío, se respeta el
  // comentario que cada fila trajo del Excel.
  useEffect(() => {
    if (step !== "asignacion" || !applyToAll) return;
    const clientesForBulk = isClientLevelType(bulkTipo, typesById)
      ? bulkClientes
      : [];
    setRows((current) =>
      current.map((row) => ({
        ...row,
        excTipoId: bulkTipo,
        clienteIds: clientesForBulk,
        comentario: bulkComentario !== "" ? bulkComentario : row.comentario,
      }))
    );
  }, [applyToAll, bulkTipo, bulkClientes, bulkComentario, step, typesById]);

  const updateRow = (index: number, patch: Partial<AssignmentRow>) => {
    setRows((current) =>
      current.map((row, position) => {
        if (position !== index) return row;
        const next = { ...row, ...patch };
        if (
          patch.excTipoId !== undefined &&
          !isClientLevelType(next.excTipoId, typesById)
        ) {
          next.clienteIds = [];
        }
        return next;
      })
    );
  };

  const validRows = rows.filter((row) => row.rutValido);
  const invalidRutCount = rows.length - validRows.length;
  const rowReady = (row: AssignmentRow): boolean => {
    if (!row.rutValido) return true; // se rechaza en backend, no bloquea el envío
    if (!row.excTipoId) return false;
    if (!row.comentario.trim()) return false;
    if (
      isClientLevelType(row.excTipoId, typesById) &&
      row.clienteIds.length === 0
    )
      return false;
    return true;
  };
  const pendingRows = rows.filter((row) => !rowReady(row)).length;
  const canProcess =
    validRows.length > 0 && pendingRows === 0 && !catalogsError;

  const process = async () => {
    if (!canProcess) return;
    setLoading(true);
    setError("");
    try {
      const registros = rows.map((row) => ({
        rut: row.rutRaw,
        nombres: toUpperName(row.nombres),
        apellidos: toUpperName(row.apellidos),
        excTipoId: row.excTipoId,
        clienteIds: isClientLevelType(row.excTipoId, typesById)
          ? row.clienteIds
          : [],
        comentario: toUpperComment(row.comentario),
      }));
      const response = await processInhabilitadosNomina({
        fileName,
        sheetName: selectedSheet,
        registros,
      });
      setResult(response);
      setResultFilter("all");
      setResultPage(0);
      setStep("result");
      onLoaded && onLoaded();
    } catch (processError: any) {
      setError(
        processError?.response?.data?.message ??
          processError?.message ??
          "No fue posible cargar la nómina de inhabilitados."
      );
    } finally {
      setLoading(false);
    }
  };

  // Exporta el resultado a Excel con formato: hoja 1 "Detalle" (con estados
  // coloreados) y hoja 2 "Resumen". Deja evidencia de qué se cargó y su estado.
  const downloadResult = () => {
    if (!result) return;
    const resumen = result.resumen;

    // --- Hoja 1: Detalle ---
    const detalleData = result.detalle.map((item) => ({
      RUT: item.rut,
      "RUT normalizado": item.rutNormalizado,
      Nombre: item.nombre,
      Tipo: item.tipo,
      Cliente: item.cliente,
      Comentario: item.comentario,
      Estado: item.estado,
      Detalle: item.mensaje,
      "Persona creada": item.personaCreada ? "Sí" : "No",
    }));
    const detalleSheet = XLSXStyle.utils.json_to_sheet(detalleData);
    styleResultSheet(detalleSheet, {
      widths: [14, 16, 26, 22, 20, 34, 20, 42, 14],
      estadoCol: 6,
    });

    // --- Hoja 2: Resumen ---
    const resumenData = [
      { Métrica: "Total registros", Valor: resumen.totalRegistros },
      { Métrica: "Inhabilitados (creados)", Valor: resumen.creados },
      { Métrica: "Personas creadas", Valor: resumen.personasCreadas },
      { Métrica: "Personas vinculadas", Valor: resumen.personasVinculadas },
      { Métrica: "Duplicados", Valor: resumen.duplicados },
      { Métrica: "RUT inválido", Valor: resumen.rutInvalidos },
      { Métrica: "Tipo inválido", Valor: resumen.tipoInvalidos },
      { Métrica: "Cliente requerido", Valor: resumen.clienteRequerido },
      { Métrica: "Cliente inválido", Valor: resumen.clienteInvalidos },
      { Métrica: "Comentario requerido", Valor: resumen.comentarioRequerido },
      { Métrica: "Errores", Valor: resumen.errores },
    ];
    const resumenSheet = XLSXStyle.utils.json_to_sheet(resumenData);
    styleResultSheet(resumenSheet, { widths: [30, 12], boldFirstCol: true });

    const workbook = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(workbook, detalleSheet, "Detalle");
    XLSXStyle.utils.book_append_sheet(workbook, resumenSheet, "Resumen");

    const base = fileName
      ? fileName.replace(/\.[^.]+$/, "")
      : "nomina_inhabilitados";
    const stamp = new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[:T]/g, "-");
    XLSXStyle.writeFile(workbook, `resultado_${base}_${stamp}.xlsx`);
  };

  // --- Paginación tabla de asignación ---
  const assignTotalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const assignStart = assignPage * PAGE_SIZE;
  const assignRows = rows.slice(assignStart, assignStart + PAGE_SIZE);

  // --- Resultado ---
  const detalle = result?.detalle ?? [];
  const filteredDetalle =
    resultFilter === "all"
      ? detalle
      : detalle.filter((item) => item.estado === resultFilter);
  const resultTotalPages = Math.max(
    1,
    Math.ceil(filteredDetalle.length / PAGE_SIZE)
  );
  const resultStart = resultPage * PAGE_SIZE;
  const resultRows = filteredDetalle.slice(resultStart, resultStart + PAGE_SIZE);

  const selectResultFilter = (filter: string) => {
    setResultFilter(filter);
    setResultPage(0);
  };

  const statusModifier = (value: string): string =>
    (value ?? "").toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");

  const renderHeader = () => (
    <div className="inhab-nomina__titlebar">
      <div>
        <span className="inhab-nomina__eyebrow">Carga masiva</span>
        <h2>Cargar Nómina de Inhabilitados</h2>
      </div>
      <button
        type="button"
        className="inhab-nomina__icon-button"
        onClick={close}
        aria-label="Cerrar"
      >
        <CloseIcon />
      </button>
    </div>
  );

  return (
    <Dialog
      header={renderHeader}
      visible={visible}
      onHide={close}
      closable={false}
      draggable={false}
      modal
      className="inhab-nomina"
      style={{ width: "min(1180px, 96vw)", maxHeight: "calc(100vh - 24px)" }}
      contentStyle={{ padding: 0 }}
    >
      <div className="inhab-nomina__steps" aria-label="Progreso">
        {["Archivo y match", "Inhabilitación", "Resultado"].map(
          (label, index) => {
            const activeIndex =
              step === "mapping" ? 0 : step === "asignacion" ? 1 : 2;
            return (
              <div
                className={`inhab-nomina__step ${
                  index <= activeIndex ? "is-active" : ""
                }`}
                key={label}
              >
                <span>{index + 1}</span>
                {label}
              </div>
            );
          }
        )}
      </div>

      <div className="inhab-nomina__body">
        {catalogsError && (
          <div className="inhab-nomina__alert">
            <ErrorOutlineIcon />
            {catalogsError}
          </div>
        )}
        {error && (
          <div className="inhab-nomina__alert">
            <ErrorOutlineIcon />
            {error}
          </div>
        )}

        {step === "mapping" && (
          <>
            <section className="inhab-nomina__file-row">
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFile}
                hidden
              />
              <button
                type="button"
                className="inhab-nomina__file-picker"
                onClick={() => inputRef.current?.click()}
              >
                {fileName ? (
                  <InsertDriveFileOutlinedIcon />
                ) : (
                  <CloudUploadOutlinedIcon />
                )}
                <span>
                  <strong>{fileName || "Seleccionar archivo Excel"}</strong>
                  <small>Formato .xlsx o .xls · máximo 10 MB</small>
                </span>
              </button>
            </section>

            {workbook && (
              <DndContext
                sensors={sensors}
                collisionDetection={pointerWithin}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={() => setActiveHeader("")}
              >
                <section className="inhab-nomina__mapping">
                  <div className="inhab-nomina__mapping-col">
                    <div className="inhab-nomina__sheet-bar">
                      <label className="inhab-nomina__sheet-select">
                        <span>Hoja a procesar</span>
                        {sheetNames.length === 1 ? (
                          <div className="inhab-nomina__sheet-single">
                            {sheetNames[0]}
                          </div>
                        ) : (
                          <SearchSelect
                            value={selectedSheet}
                            options={sheetSelectOptions}
                            onChange={(code) => selectSheet(code)}
                            placeholder="Selecciona una hoja"
                            clearable={false}
                            ariaLabel="Hoja a procesar"
                          />
                        )}
                      </label>
                      {sheetData && (
                        <div className="inhab-nomina__file-metadata">
                          <strong>
                            {sheetData.rows.length.toLocaleString("es-CL")}
                          </strong>{" "}
                          filas
                          <span>
                            Encabezado detectado en fila {sheetData.headerRow}
                          </span>
                        </div>
                      )}
                    </div>

                    {sheetData && (
                      <div className="inhab-nomina__mapping-panel">
                        <div className="inhab-nomina__panel-heading">
                          <div>
                            <h3>Columnas de la hoja</h3>
                            <p>Arrastra una columna hacia su equivalente.</p>
                          </div>
                          <span>{sheetData.headers.length} columnas</span>
                        </div>
                        <label className="inhab-nomina__search">
                          <SearchIcon />
                          <input
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Buscar columna del archivo"
                          />
                        </label>
                        <div className="inhab-nomina__source-list">
                          {visibleSourceHeaders.map((header) => (
                            <SourceItem
                              key={header}
                              header={header}
                              mapped={mappedSources.has(header)}
                            />
                          ))}
                          {visibleSourceHeaders.length === 0 && (
                            <p className="inhab-nomina__source-empty">
                              No hay columnas que coincidan con la búsqueda.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {sheetData && (
                    <div className="inhab-nomina__mapping-panel inhab-nomina__mapping-panel--system">
                      <div className="inhab-nomina__panel-heading">
                        <div>
                          <h3>Columnas del sistema</h3>
                          <p>
                            Solo RUT es obligatorio. Cada columna se usa una
                            sola vez.
                          </p>
                        </div>
                        <span>{mappingPairs.length} matches</span>
                      </div>
                      <div className="inhab-nomina__target-list">
                        {/* Grupo 1: RUT (obligatorio) */}
                        <section className="inhab-nomina__group inhab-nomina__group--rut">
                          <header className="inhab-nomina__group-head">
                            <span className="inhab-nomina__group-icon">
                              <BadgeOutlinedIcon />
                            </span>
                            <div className="inhab-nomina__group-titles">
                              <strong>Identificación</strong>
                              <small>
                                El RUT identifica a cada persona a inhabilitar.
                              </small>
                            </div>
                            <span className="inhab-nomina__group-badge is-required">
                              Obligatorio
                            </span>
                          </header>
                          <div className="inhab-nomina__group-body">
                            <div
                              className="inhab-nomina__seg"
                              role="tablist"
                              aria-label="Formato del RUT en el Excel"
                            >
                              {RUT_FORMAT_OPTIONS.map((option) => (
                                <button
                                  type="button"
                                  role="tab"
                                  aria-selected={rutFormat === option.value}
                                  className={`inhab-nomina__seg-btn ${
                                    rutFormat === option.value
                                      ? "is-active"
                                      : ""
                                  }`}
                                  onClick={() => changeRutFormat(option.value)}
                                  key={option.value}
                                >
                                  <strong>{option.label}</strong>
                                  <small>{option.hint}</small>
                                </button>
                              ))}
                            </div>
                            <div className="inhab-nomina__name-fields">
                              {RUT_FIELDS_BY_FORMAT[rutFormat].map((key) =>
                                renderTargetSlot(key)
                              )}
                            </div>
                          </div>
                        </section>

                        {/* Grupo 2: Nombre (opcional, con selector de formato) */}
                        <section className="inhab-nomina__group inhab-nomina__group--nombre">
                          <header className="inhab-nomina__group-head">
                            <span className="inhab-nomina__group-icon">
                              <PersonOutlineIcon />
                            </span>
                            <div className="inhab-nomina__group-titles">
                              <strong>Nombre del candidato</strong>
                              <small>
                                ¿Cómo viene el nombre en tu Excel? Elige el
                                formato y aparecerán solo esas columnas.
                              </small>
                            </div>
                            <span className="inhab-nomina__group-badge">
                              Opcional
                            </span>
                          </header>
                          <div className="inhab-nomina__group-body">
                            <div
                              className="inhab-nomina__seg"
                              role="tablist"
                              aria-label="Formato del nombre en el Excel"
                            >
                              {NAME_FORMAT_OPTIONS.map((option) => (
                                <button
                                  type="button"
                                  role="tab"
                                  aria-selected={nameFormat === option.value}
                                  className={`inhab-nomina__seg-btn ${
                                    nameFormat === option.value
                                      ? "is-active"
                                      : ""
                                  }`}
                                  onClick={() => changeNameFormat(option.value)}
                                  key={option.value}
                                >
                                  <strong>{option.label}</strong>
                                  <small>{option.hint}</small>
                                </button>
                              ))}
                            </div>
                            <div className="inhab-nomina__name-fields">
                              {NAME_FIELDS_BY_FORMAT[nameFormat].map((key) =>
                                renderTargetSlot(key)
                              )}
                            </div>
                          </div>
                        </section>

                        {/* Grupo 3: Comentario (opcional) */}
                        <section className="inhab-nomina__group inhab-nomina__group--comentario">
                          <header className="inhab-nomina__group-head">
                            <span className="inhab-nomina__group-icon">
                              <ChatBubbleOutlineIcon />
                            </span>
                            <div className="inhab-nomina__group-titles">
                              <strong>Comentario</strong>
                              <small>
                                Motivo del bloqueo, tomado por registro.
                              </small>
                            </div>
                            <span className="inhab-nomina__group-badge">
                              Opcional
                            </span>
                          </header>
                          <div className="inhab-nomina__group-body">
                            {renderTargetSlot("comentario", true)}
                          </div>
                        </section>
                      </div>
                    </div>
                  )}
                </section>

                <DragOverlay dropAnimation={null}>
                  {activeHeader ? (
                    <div className="inhab-nomina__source inhab-nomina__source--overlay">
                      <span className="inhab-nomina__drag-handle">⋮⋮</span>
                      <span className="inhab-nomina__source-label">
                        {activeHeader}
                      </span>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}
          </>
        )}

        {step === "asignacion" && (
          <section className="inhab-nomina__assign">
            <div className={`inhab-nomina__bulk ${applyToAll ? "is-on" : ""}`}>
              <div className="inhab-nomina__bulk-head">
                <label className="inhab-nomina__switch">
                  <input
                    type="checkbox"
                    checked={applyToAll}
                    onChange={(event) => setApplyToAll(event.target.checked)}
                  />
                  <span className="inhab-nomina__switch-track">
                    <span className="inhab-nomina__switch-thumb" />
                  </span>
                  <span className="inhab-nomina__switch-label">
                    Aplicar a todos
                  </span>
                </label>
                <small>
                  {applyToAll
                    ? "El tipo, cliente y comentario definidos aquí se aplican a todos los registros."
                    : "Configura el tipo, cliente y comentario en cada registro de la tabla."}
                </small>
              </div>
              {applyToAll && (
                <div className="inhab-nomina__bulk-controls">
                  <label>
                    <span>Tipo de inhabilitación</span>
                    <SearchSelect
                      value={bulkTipo}
                      options={typeSelectOptions}
                      onChange={(code) => {
                        setBulkTipo(code);
                        if (!isClientLevelType(code, typesById))
                          setBulkClientes([]);
                      }}
                      placeholder="Seleccionar"
                      searchPlaceholder="Buscar tipo"
                      ariaLabel="Tipo de inhabilitación"
                    />
                  </label>
                  {isClientLevelType(bulkTipo, typesById) && (
                    <label>
                      <span>Cliente(s)</span>
                      <MultiSearchSelect
                        value={bulkClientes}
                        options={clientSelectOptions}
                        onChange={(codes) => setBulkClientes(codes)}
                        placeholder="Seleccionar"
                        searchPlaceholder="Buscar cliente"
                        ariaLabel="Clientes"
                      />
                    </label>
                  )}
                  <label className="inhab-nomina__bulk-comment">
                    <span>Comentario</span>
                    <input
                      value={bulkComentario}
                      onChange={(event) =>
                        setBulkComentario(event.target.value.toUpperCase())
                      }
                      maxLength={300}
                      placeholder="Motivo de la inhabilitación"
                    />
                  </label>
                </div>
              )}
            </div>

            <div className="inhab-nomina__assign-heading">
              <div>
                <h3>Registros detectados</h3>
                <p>
                  Asigna el tipo de inhabilitación, cliente (si corresponde) y
                  comentario. El comentario es obligatorio.
                </p>
              </div>
              <div className="inhab-nomina__assign-counters">
                <span className="ok">{validRows.length} válidos</span>
                {invalidRutCount > 0 && (
                  <span className="bad">{invalidRutCount} RUT inválido</span>
                )}
                {pendingRows > 0 && (
                  <span className="warn">{pendingRows} sin completar</span>
                )}
              </div>
            </div>

            <div className="inhab-nomina__table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>RUT</th>
                    <th>Nombre</th>
                    <th>Tipo de inhabilitación</th>
                    <th>Cliente</th>
                    <th>Comentario</th>
                  </tr>
                </thead>
                <tbody>
                  {assignRows.map((row, position) => {
                    const index = assignStart + position;
                    const clientLevel = isClientLevelType(
                      row.excTipoId,
                      typesById
                    );
                    return (
                      <tr
                        key={`${row.rutRaw}-${index}`}
                        className={!row.rutValido ? "is-invalid" : ""}
                      >
                        <td>{index + 1}</td>
                        <td>
                          {row.rutFormateado || <em>Sin dato</em>}
                          {!row.rutValido && (
                            <span className="inhab-nomina__rut-flag">
                              RUT inválido
                            </span>
                          )}
                        </td>
                        <td>
                          {`${row.nombres} ${row.apellidos}`.trim() || (
                            <em>Sin dato</em>
                          )}
                        </td>
                        <td>
                          <SearchSelect
                            value={row.excTipoId}
                            options={typeSelectOptions}
                            disabled={applyToAll || !row.rutValido}
                            onChange={(code) =>
                              updateRow(index, { excTipoId: code })
                            }
                            placeholder="Seleccionar"
                            searchPlaceholder="Buscar tipo"
                            ariaLabel="Tipo de inhabilitación"
                          />
                        </td>
                        <td>
                          {clientLevel ? (
                            <MultiSearchSelect
                              value={row.clienteIds}
                              options={clientSelectOptions}
                              disabled={applyToAll || !row.rutValido}
                              onChange={(codes) =>
                                updateRow(index, { clienteIds: codes })
                              }
                              placeholder="Seleccionar"
                              searchPlaceholder="Buscar cliente"
                              ariaLabel="Clientes"
                            />
                          ) : (
                            <span className="inhab-nomina__na">No aplica</span>
                          )}
                        </td>
                        <td>
                          <input
                            value={row.comentario}
                            disabled={applyToAll || !row.rutValido}
                            maxLength={300}
                            placeholder="Comentario"
                            onChange={(event) =>
                              updateRow(index, {
                                comentario: event.target.value.toUpperCase(),
                              })
                            }
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="inhab-nomina__pagination">
              <span>
                Mostrando {rows.length ? assignStart + 1 : 0}–
                {Math.min(assignStart + PAGE_SIZE, rows.length)} de{" "}
                {rows.length.toLocaleString("es-CL")}
              </span>
              <div>
                <button
                  type="button"
                  disabled={assignPage === 0}
                  onClick={() => setAssignPage((page) => Math.max(0, page - 1))}
                >
                  Anterior
                </button>
                <strong>
                  {assignPage + 1} / {assignTotalPages}
                </strong>
                <button
                  type="button"
                  disabled={assignPage + 1 >= assignTotalPages}
                  onClick={() =>
                    setAssignPage((page) =>
                      Math.min(assignTotalPages - 1, page + 1)
                    )
                  }
                >
                  Siguiente
                </button>
              </div>
            </div>
          </section>
        )}

        {step === "result" && result && (
          <section className="inhab-nomina__result">
            <div className="inhab-nomina__result-heading">
              <div className="inhab-nomina__result-icon">
                <PersonRemoveOutlinedIcon />
              </div>
              <div>
                <h3>Nómina procesada</h3>
                <p>{result.message}</p>
              </div>
            </div>
            <div className="inhab-nomina__summary">
              <button
                type="button"
                className={`inhab-nomina__summary-card ${
                  resultFilter === "all" ? "is-active" : ""
                }`}
                onClick={() => selectResultFilter("all")}
              >
                <strong>{result.resumen.totalRegistros}</strong>
                <span>Total</span>
              </button>
              <button
                type="button"
                className={`inhab-nomina__summary-card ok ${
                  resultFilter === "CREADO" ? "is-active" : ""
                }`}
                onClick={() => selectResultFilter("CREADO")}
              >
                <strong>{result.resumen.creados}</strong>
                <span>Inhabilitados</span>
              </button>
              <button
                type="button"
                className={`inhab-nomina__summary-card info ${
                  resultFilter === "personas" ? "is-active" : ""
                }`}
                onClick={() => selectResultFilter("all")}
              >
                <strong>{result.resumen.personasCreadas}</strong>
                <span>Personas creadas</span>
              </button>
              <button
                type="button"
                className={`inhab-nomina__summary-card warn ${
                  resultFilter === "DUPLICADO" ? "is-active" : ""
                }`}
                onClick={() => selectResultFilter("DUPLICADO")}
              >
                <strong>{result.resumen.duplicados}</strong>
                <span>Duplicados</span>
              </button>
              <button
                type="button"
                className={`inhab-nomina__summary-card bad ${
                  resultFilter === "RUT_INVALIDO" ? "is-active" : ""
                }`}
                onClick={() => selectResultFilter("RUT_INVALIDO")}
              >
                <strong>{result.resumen.rutInvalidos}</strong>
                <span>RUT inválido</span>
              </button>
              <button
                type="button"
                className={`inhab-nomina__summary-card bad ${
                  resultFilter === "ERROR" ? "is-active" : ""
                }`}
                onClick={() => selectResultFilter("ERROR")}
              >
                <strong>
                  {result.resumen.errores +
                    result.resumen.tipoInvalidos +
                    result.resumen.clienteRequerido +
                    result.resumen.clienteInvalidos +
                    result.resumen.comentarioRequerido}
                </strong>
                <span>Con problemas</span>
              </button>
            </div>

            <div className="inhab-nomina__table-wrap inhab-nomina__result-table">
              <table>
                <thead>
                  <tr>
                    <th>Fila</th>
                    <th>RUT</th>
                    <th>Nombre</th>
                    <th>Tipo</th>
                    <th>Cliente</th>
                    <th>Estado</th>
                    <th>Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {resultRows.map((item) => (
                    <tr key={`${item.fila}-${item.rutNormalizado}`}>
                      <td>{item.fila}</td>
                      <td>{item.rutNormalizado || item.rut || <em>Sin dato</em>}</td>
                      <td>{item.nombre || <em>Sin dato</em>}</td>
                      <td>
                        {item.tipo || <em>—</em>}
                        {item.personaCreada && (
                          <span className="inhab-nomina__tag">Persona nueva</span>
                        )}
                      </td>
                      <td>{item.cliente || <em>—</em>}</td>
                      <td
                        className={`inhab-nomina__status--${statusModifier(
                          item.estado
                        )}`}
                      >
                        {item.estado}
                      </td>
                      <td>{item.mensaje || <em>—</em>}</td>
                    </tr>
                  ))}
                  {resultRows.length === 0 && (
                    <tr>
                      <td className="inhab-nomina__empty-result" colSpan={7}>
                        No hay registros para este filtro.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="inhab-nomina__pagination">
              <span>
                Mostrando {filteredDetalle.length ? resultStart + 1 : 0}–
                {Math.min(resultStart + PAGE_SIZE, filteredDetalle.length)} de{" "}
                {filteredDetalle.length.toLocaleString("es-CL")}
              </span>
              <div>
                <button
                  type="button"
                  disabled={resultPage === 0}
                  onClick={() => setResultPage((page) => Math.max(0, page - 1))}
                >
                  Anterior
                </button>
                <strong>
                  {resultPage + 1} / {resultTotalPages}
                </strong>
                <button
                  type="button"
                  disabled={resultPage + 1 >= resultTotalPages}
                  onClick={() =>
                    setResultPage((page) =>
                      Math.min(resultTotalPages - 1, page + 1)
                    )
                  }
                >
                  Siguiente
                </button>
              </div>
            </div>
          </section>
        )}
      </div>

      <footer className="inhab-nomina__footer">
        {step === "mapping" && (
          <>
            <button
              type="button"
              className="inhab-nomina__secondary"
              onClick={close}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="inhab-nomina__primary"
              disabled={!canAssign}
              onClick={goToAssignment}
            >
              Continuar
            </button>
          </>
        )}
        {step === "asignacion" && (
          <>
            <button
              type="button"
              className="inhab-nomina__secondary"
              onClick={() => setStep("mapping")}
            >
              <ArrowBackIcon />
              Volver al match
            </button>
            <button
              type="button"
              className="inhab-nomina__primary"
              disabled={!canProcess || loading}
              onClick={process}
            >
              {loading ? "Procesando…" : "Inhabilitar nómina"}
            </button>
          </>
        )}
        {step === "result" && (
          <>
            <button
              type="button"
              className="inhab-nomina__excel"
              onClick={downloadResult}
            >
              <FileDownloadOutlinedIcon />
              Descargar resultados
            </button>
            <button
              type="button"
              className="inhab-nomina__primary"
              onClick={close}
            >
              Cerrar
            </button>
          </>
        )}
      </footer>
    </Dialog>
  );
};

export default CargaNominaInhabilitados;
